import { FC, useMemo } from "react";
import { Control, useController } from "react-hook-form";
import styled from "styled-components";

import Text from "@/components/Text";
import { FormDataT } from "./TestingAudienceForm";
import { AudiencableChannel } from "@/types/resources";
import TestingAudienceChannelCard from "@/components/TestingAudienceChannelCard";

const Wrap = styled.div`
  margin-top: 40px;
`;

const Heading = styled(Text)``;

const SubHeading = styled(Text)`
  margin-top: 16px;
  margin-bottom: 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  control: Control<FormDataT>;
}

const SelectedChannelsList: FC<Props> = ({ control }) => {
  const {
    field: { onChange, value: channels },
  } = useController({
    name: "channels",
    control,
  });

  const internalChannels = useMemo(
    () => channels.filter((c) => c.is_internal),
    [channels]
  );

  const externalChannels = useMemo(
    () => channels.filter((c) => !c.is_internal),
    [channels]
  );

  const onRemove = (channel: AudiencableChannel) => {
    onChange(channels.filter((c) => c.external_id !== channel.external_id));
  };

  const renderChannels = (channels: AudiencableChannel[]) =>
    channels.map((c) => (
      <TestingAudienceChannelCard
        channel={c}
        key={c.external_id}
        onRemove={() => onRemove(c)}
      />
    ));

  if (channels.length === 0) return null;

  return (
    <Wrap>
      <Heading $type="caption">Audience channels</Heading>
      {!!internalChannels.length && (
        <>
          <SubHeading $color="gray" $type="smallHeadline">
            Internal channels list
          </SubHeading>
          <List>{renderChannels(internalChannels)}</List>
        </>
      )}
      {!!externalChannels.length && (
        <>
          <SubHeading $color="gray" $type="smallHeadline">
            External channels list
          </SubHeading>
          <List>{renderChannels(externalChannels)}</List>
        </>
      )}
    </Wrap>
  );
};

export default SelectedChannelsList;
